import type { Dispatch, SetStateAction } from "react";
import type { UnsavedRow } from "./types";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TableDimensionInput } from "./TableDimensionInput";
import { v4 as uuid } from "uuid";
import { AccessSeparator } from "@acdc2/ui/components/access-separator";
import {
  FormControl,
  FormField,
  FormFieldset,
  FormLegend,
} from "@acdc2/ui/components/access-form";

type Props = {
  rows: UnsavedRow[];
  setRows: Dispatch<SetStateAction<UnsavedRow[]>>;
};

export function TableRowListFormField({ rows, setRows }: Props) {
  const { t } = useTranslation();
  const [unsavedRows, setUnsavedRows] = useState<UnsavedRow[]>([]);

  const createUnsavedRow = () => {
    setUnsavedRows((oldRows) => [...oldRows, { uuid: uuid(), height: 0 }]);
  };

  const removeUnsavedRow = (rowId: string) => {
    setUnsavedRows((oldRows) => oldRows.filter((row) => row.uuid !== rowId));
  };

  const onSave = (index: number, newHeight: number) => {
    setRows((oldRows) => {
      const newRows = [...oldRows];
      newRows[index].height = newHeight;
      return newRows;
    });
  };

  const onDelete = (index: number) => {
    setRows((oldRows) => {
      const newRows = [...oldRows];
      newRows.splice(index, 1);
      return newRows;
    });
  };

  return (
    <FormFieldset>
      <FormLegend>{t("TableRowListFormField.title")}</FormLegend>
      {rows.map((row, index) => (
        <FormField key={row.uuid} className="flex flex-col" name="tableRowList">
          <FormControl asChild>
            <TableDimensionInput
              defaultValue={row.height}
              onDelete={() => onDelete(index)}
              onSave={(newHeight: number) => onSave(index, newHeight)}
            />
          </FormControl>
        </FormField>
      ))}

      {unsavedRows.length > 0 && (
        <AccessSeparator
          className="my-1"
          title={t("MetadataAuthorAccordionItem.separator")}
        />
      )}
      {unsavedRows.map((row) => (
        <TableDimensionInput
          key={row.uuid}
          defaultValue={row.height}
          onDelete={() => removeUnsavedRow(row.uuid)}
          onSave={(newHeight: number) => {
            setRows((oldRows) => [...oldRows, { ...row, height: newHeight }]);
            removeUnsavedRow(row.uuid);
          }}
        />
      ))}
      <AccessButton type="button" onClick={() => createUnsavedRow()}>
        {t("TableRowListFormField.labels.add")}
      </AccessButton>
    </FormFieldset>
  );
}
